import { useEffect, useMemo, useState } from "react";

import { drawLabel, useHotspotCanvas } from "@vericus/cadmus-common";

import { useAppDispatch, useAppSelector } from "@/data/hooks";
import { selectAnswerSnapshot, setLocalSnapshot } from "@/features/authority";
import { PromptImage, QuestionFieldFragment } from "@/generated/graphql";
import { useAnswerBlock } from "@/graphql/hooks";

interface PinningOption {
  x: number;
  y: number;
  fieldIdentifier: string;
  label: string;
  isOnCanvas: boolean;
}

const PIN_ICON_WIDTH = 20;

/**
 * Custom hook to manage the state and interactions of the hotspot response form.
 * It handles the rendering of draggable options on a canvas, scaling, and user interactions.
 *
 * @param answerBlockId - The ID of the answer block.
 * @param questionFields - The fields of the question.
 * @param isReadonly - Boolean indicating if the form is read-only.
 * @returns An object containing refs, state, and event handlers for the form.
 */
export const useHotspotPositionObjectResponseForm = (
  answerBlockId: string,
  questionFields: QuestionFieldFragment[],
  isReadonly: boolean
) => {
  const dispatch = useAppDispatch();
  const { data } = useAnswerBlock(answerBlockId);
  const snapshot = useAppSelector((state) =>
    selectAnswerSnapshot(state, answerBlockId)
  );
  const promptImage = useMemo(() => data?.question?.body?.promptImage, [data]);

  const [currentScale, setCurrentScale] = useState<number>(1);
  const [options, setOptions] = useState<PinningOption[]>([]);
  const optionsNotOnCanvas = options.filter((option) => !option.isOnCanvas);
  const [currentFieldIdentifier, setCurrentFieldIdentifier] = useState<
    string | null
  >(null);
  const [isHoveringPin, setIsHoveringPin] = useState(false);

  /**
   * Checks if the mouse coordinates are within the bounds of a pin and its label.
   *
   * @param mouseX - The X coordinate of the mouse.
   * @param mouseY - The Y coordinate of the mouse.
   * @param option - The draggable option to check against.
   * @param ctx - The canvas rendering context.
   * @returns Boolean indicating if the mouse is within the pin and label bounds.
   */
  const isWithinPinBounds = (
    mouseX: number,
    mouseY: number,
    option: PinningOption,
    ctx: CanvasRenderingContext2D
  ) => {
    const labelWidth = ctx.measureText(option.label).width;
    const labelHeight = 20;
    const fullLabelWidth = labelWidth + PIN_ICON_WIDTH + 30;
    const labelOnRight = option.x - fullLabelWidth < 0;

    return (
      mouseX >= (labelOnRight ? option.x - 10 : option.x - fullLabelWidth) &&
      mouseX <= (labelOnRight ? option.x + fullLabelWidth : option.x + 10) &&
      mouseY >= option.y - labelHeight &&
      mouseY <= option.y + labelHeight
    );
  };

  const { canvasRef, wrapperRef } = useHotspotCanvas(
    promptImage as PromptImage,
    (ctx, scale) => {
      setCurrentScale(scale);
      options.forEach((option) => {
        if (option.isOnCanvas) drawLabel(ctx, option.x, option.y, option.label);
      });
    }
  );

  useEffect(() => {
    if (canvasRef?.current && promptImage?.url && currentScale !== null) {
      const canvas = canvasRef.current!;
      const ctx = canvas.getContext("2d")!;
      const img = new Image();
      img.src = promptImage.url;
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(
          img,
          0,
          0,
          canvas.width / window.devicePixelRatio,
          canvas.height / window.devicePixelRatio
        );
        options.forEach((option) => {
          if (option.isOnCanvas)
            drawLabel(ctx, option.x, option.y, option.label);
        });
      };
    }
  }, [promptImage, options, currentScale, canvasRef]);

  useEffect(() => {
    if (currentScale !== null) {
      setOptions(
        questionFields.map((field, index) => {
          const savedPosition = snapshot?.answerFields?.[field.identifier];
          const scaledX = savedPosition
            ? parseFloat(savedPosition[0] ?? "") * currentScale
            : 0;
          const scaledY = savedPosition
            ? parseFloat(savedPosition[1] ?? "") * currentScale
            : 0;
          return {
            x: scaledX,
            y: scaledY,
            fieldIdentifier: field.identifier,
            label:
              "label" in field.interaction
                ? field.interaction.label || `Option ${index + 1}`
                : `Option ${index + 1}`,
            isOnCanvas: !!savedPosition,
          };
        })
      );
    }
  }, [currentScale, questionFields, snapshot]);

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (isReadonly) return;

    const rect = canvasRef.current!.getBoundingClientRect();
    const x = (event.clientX - rect.left) / currentScale;
    const y = (event.clientY - rect.top) / currentScale;

    const ctx = canvasRef.current!.getContext("2d")!;

    if (!currentFieldIdentifier) {
      const clickedOption = options.find(
        (option) =>
          option.isOnCanvas &&
          isWithinPinBounds(x * currentScale, y * currentScale, option, ctx)
      );

      if (clickedOption) {
        setCurrentFieldIdentifier(clickedOption.fieldIdentifier);
      }
      
      return;
    }

    setOptions((prevOptions) =>
      prevOptions.map((option) =>
        option.fieldIdentifier === currentFieldIdentifier
          ? { ...option, x, y, isOnCanvas: true }
          : option
      )
    );

    const answerFields = options.reduce(
      (acc, option) => {
        if (snapshot?.answerFields?.[option.fieldIdentifier]) {
          acc[option.fieldIdentifier] =
            snapshot.answerFields[option.fieldIdentifier] || [];
        }
        return acc;
      },
      {} as Record<string, string[]>
    );

    dispatch(
      setLocalSnapshot({
        answerBlockId,
        snapshot: {
          version: snapshot?.version ? snapshot.version + 1 : 1,
          answerFields: {
            ...answerFields,
            [currentFieldIdentifier]: [x.toString(), y.toString()],
          },
        },
      })
    );

    setCurrentFieldIdentifier(null);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const ctx = canvasRef.current.getContext("2d")!;

    if (currentFieldIdentifier) {
      setOptions((prevOptions) =>
        prevOptions.map((option) =>
          option.fieldIdentifier === currentFieldIdentifier
            ? {
                ...option,
                x: x,
                y: y,
                isOnCanvas: true,
              }
            : option
        )
      );
    } else {
      const hoveringOption = options.some(
        (option) => option.isOnCanvas && isWithinPinBounds(x, y, option, ctx)
      );

      setIsHoveringPin(hoveringOption);
    }
  };

  return {
    canvasRef,
    wrapperRef,
    promptImage,
    options,
    isHoveringPin,
    optionsNotOnCanvas,
    currentFieldIdentifier,
    setCurrentFieldIdentifier,
    handleCanvasClick,
    handleMouseMove,
  };
};
